import {ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgProgressbar, provideNgProgressOptions} from "ngx-progressbar";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from "@angular/common/http";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {DatePipe, registerLocaleData} from "@angular/common";
import localeDe from '@angular/common/locales/de';
import {MatPaginatorIntl} from "@angular/material/paginator";
import {getGermanPaginatorIntl} from '../assets/i18n/german-paginator-intl';
import {OAuthModule, OAuthStorage} from "angular-oauth2-oidc";
import {ComponentsModule} from "./components/components.module";
import {GlobalErrorHandler} from "./services/error-handling/global-error-handler";
import {NgProgressHttp, progressInterceptor} from "ngx-progressbar/http";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from "@angular/material/form-field";
import {MAT_CHIPS_DEFAULT_OPTIONS} from "@angular/material/chips";
import {COMMA, SPACE} from "@angular/cdk/keycodes";
import {TokenInterceptor} from "ngx-auth-util-lib";
import { forkJoin, map, Observable } from 'rxjs';
import deepmerge from 'deepmerge';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';

export class HekaTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {
  }

  getTranslation(lang: string): Observable<any> {
    const global = this.http.get(`assets/i18n-global/${lang}.json`);
    const local = this.http.get(`assets/i18n/${lang}.json`);

    return forkJoin([global, local]).pipe(
      map(([globalTrans, localTrans]) => {
        // Merge translations (local overwrites global in case of conflict)
        return deepmerge(globalTrans, localTrans);
      })
    );
  }
}

export function createTranslateLoader(http: HttpClient) {
  return new HekaTranslateLoader(http);
}

registerLocaleData(localeDe);

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.setDefaultLang('de');
    return translate.use('de').toPromise();
  };
}

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    OAuthModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'de',
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    ComponentsModule,
    NgProgressHttp, NgProgressbar
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = (appInitializerFactory)(inject(TranslateService));
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de'
    },
    DatePipe,
    {provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl()},
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true},
    {provide: OAuthStorage, useValue: localStorage},
    {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline', subscriptSizing: 'dynamic'}},
    {provide: MAT_CHIPS_DEFAULT_OPTIONS, useValue: {separatorKeyCodes: [COMMA, SPACE]}},
     {
      provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
        autoFocus: 'dialog',
        delayFocusTrap: true,
        restoreFocus: true
      }
    },

    provideNgProgressOptions({
      flat: false,
      spinner: false
    }),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([progressInterceptor])), //withInterceptorsFromDi() needed for class based interceptors
  ]
})
export class AppModule {
}
